/* ==========================================================================
   Scrolling Diagonal Lines - Asian American Forward Website
   Clean Pure CSS Implementation for Windows Animation Compatibility
   ========================================================================== */

let linesInitialized = false;
let scrollHandler = null;
let tickerElement = null;
let linesContainer = null;

/* ==========================================================================
   Scrolling Lines Initialization
   ========================================================================== */

export function initializeScrollingLines() {
  console.log('📐 [DEBUG] Initializing clean CSS scrolling diagonal lines...');
  console.log('📐 [DEBUG] Document ready state:', document.readyState);
  console.log('📐 [DEBUG] Window scroll position:', window.pageYOffset || window.scrollY);
  
  // Wait for loading to complete
  if (document.readyState === 'loading') {
    console.log('⏳ [DEBUG] Document still loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', setupScrollingLines);
  } else {
    console.log('✅ [DEBUG] Document ready, setting up lines immediately');
    setupScrollingLines();
  }
}

function setupScrollingLines() {
  console.log('🚀 [DEBUG] Setting up clean CSS scrolling lines...');
  
  // Find the diagonal separator
  tickerElement = document.querySelector('.diagonal-separator');
  if (!tickerElement) {
    console.error('❌ [DEBUG] Diagonal separator element not found');
    return;
  }
  
  console.log('✅ [DEBUG] Found diagonal separator element:', tickerElement);
  
  // Get separator position info
  const separatorRect = tickerElement.getBoundingClientRect();
  console.log('📐 [DEBUG] Separator position:', {
    top: separatorRect.top,
    left: separatorRect.left,
    width: separatorRect.width,
    height: separatorRect.height,
    documentTop: separatorRect.top + window.pageYOffset
  });
  
  // Create the lines
  createLinesHTML();
  
  // Set up scroll event listener
  setupScrollListener();
  
  linesInitialized = true;
  console.log('✅ [DEBUG] Clean CSS scrolling lines setup complete');
}

/* ==========================================================================
   HTML Structure Creation
   ========================================================================== */

function createLinesHTML() {
  console.log('📦 [DEBUG] Creating lines with pure CSS...');
  
  // Remove any existing lines container
  const existingContainer = document.getElementById('css-lines-container');
  if (existingContainer) {
    console.log('🗑️ [DEBUG] Removing existing lines container');
    existingContainer.remove();
  }
  
  // Create container for diagonal lines
  linesContainer = document.createElement('div');
  linesContainer.className = 'css-diagonal-lines-container';
  linesContainer.id = 'css-lines-container';
  
     // Set container styles - use fixed positioning to avoid affecting document height
   linesContainer.style.cssText = `
     position: fixed;
     top: 0;
     left: 0;
     width: 100%;
     height: 100vh;
     z-index: -1;
     pointer-events: none;
     overflow: visible;
   `;
  
  console.log('📦 [DEBUG] Created lines container with height:', linesContainer.style.height);
  
  // Create individual lines
  const lineConfigs = [
    { color: 'red', id: 'css-line-red', bgColor: '#8f2226' },
    { color: 'blue', id: 'css-line-blue', bgColor: '#084874' },
    { color: 'black', id: 'css-line-black', bgColor: '#000000' }
  ];
  
  lineConfigs.forEach((config, index) => {
    const line = document.createElement('div');
    line.className = `css-diagonal-line`;
    line.setAttribute('data-color', config.color);
    line.id = config.id;
    
    // Set line styles using pure CSS
    line.style.cssText = `
      position: absolute;
      width: 0px;
      height: 45px;
      background-color: ${config.bgColor};
      opacity: 0;
      z-index: -1;
      transform-origin: left center;
      transform: rotate(130deg);
      left: 0px;
      top: 0px;
    `;
    
    linesContainer.appendChild(line);
    console.log(`✨ [DEBUG] Created ${config.color} line with ID: ${line.id}`);
  });
  
  // Insert container into document body
  document.body.appendChild(linesContainer);
  console.log('📍 [DEBUG] Lines container added to body');
}

/* ==========================================================================
   Scroll Event Handling
   ========================================================================== */

function setupScrollListener() {
  console.log('📜 [DEBUG] Setting up scroll event listener...');
  
  // Remove any existing scroll listener
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
  }
  
  // Create new scroll handler
  scrollHandler = function() {
    requestAnimationFrame(updateLinesPosition);
  };
  
  // Add scroll listener
  window.addEventListener('scroll', scrollHandler, { passive: true });
  
  // Add resize listener to recalculate line positions
  window.addEventListener('resize', function() {
    requestAnimationFrame(updateLinesPosition);
  }, { passive: true });
  
  // Initial position update
  updateLinesPosition();
  
  console.log('✅ [DEBUG] Scroll listener setup complete');
}

function updateLinesPosition() {
  if (!tickerElement || !linesContainer) return;
  
  const scrollY = window.pageYOffset || window.scrollY;
  const separatorRect = tickerElement.getBoundingClientRect();
  const separatorTop = separatorRect.top + scrollY;
  
  // Find the black bar (diagonal-stripe) within the separator
  const blackBar = tickerElement.querySelector('.diagonal-stripe');
  if (!blackBar) {
    console.warn('❌ [DEBUG] Black bar (.diagonal-stripe) not found');
    return;
  }
  
  const blackBarRect = blackBar.getBoundingClientRect();
  
  // Calculate when to start animation (when separator is in center of viewport)
  const viewportCenter = window.innerHeight / 2;
  const separatorInCenter = separatorRect.top <= viewportCenter && separatorRect.bottom >= viewportCenter;
  
     // Calculate progress based on scroll position
   const startScroll = separatorTop - viewportCenter;
   const endScroll = startScroll + 660; // 660px of scroll for 10% slower animation
   const progress = Math.max(0, Math.min(1, (scrollY - startScroll) / (endScroll - startScroll)));
  
  console.log('📜 [DEBUG] Scroll update:', {
    scrollY: scrollY.toFixed(0),
    separatorTop: separatorTop.toFixed(0),
    blackBarLeft: blackBarRect.left.toFixed(0),
    blackBarWidth: blackBarRect.width.toFixed(0),
    progress: progress.toFixed(3),
    separatorInCenter: separatorInCenter
  });
  
  // Update each line
  const lines = linesContainer.querySelectorAll('.css-diagonal-line');
  lines.forEach((line, index) => {
    const staggeredProgress = Math.max(0, progress - (index * 0.1));
    
    if (staggeredProgress > 0) {
       // Calculate position and size - position lines in center of ticker text box
       const blackBarCenter = blackBarRect.left + (blackBarRect.width / 2);
       const startX = blackBarCenter + (index * 100); // Space lines horizontally apart from center
       const startY = separatorRect.top + 10; // Use viewport coordinates since container is fixed
       
       // Calculate infinite width with slower animation - use eased progress for smoother extension
       const viewportDiagonal = Math.sqrt(window.innerWidth * window.innerWidth + window.innerHeight * window.innerHeight);
       const easedProgress = staggeredProgress * staggeredProgress; // Quadratic easing for slower start
       const infiniteWidth = (viewportDiagonal * 2.5) * easedProgress; // Increased multiplier to ensure lines extend past screen edges
       
       const opacity = 1.0; // Full opacity
       
       // Apply styles directly
       line.style.left = startX + 'px';
       line.style.top = startY + 'px';
       line.style.width = infiniteWidth + 'px';
       line.style.opacity = opacity;
      
             console.log(`📏 [DEBUG] ${line.getAttribute('data-color')} line:`, {
         left: startX.toFixed(0) + 'px',
         top: startY.toFixed(0) + 'px',
         width: infiniteWidth.toFixed(0) + 'px',
         opacity: opacity,
         progress: staggeredProgress.toFixed(3),
         easedProgress: easedProgress.toFixed(3),
         blackBarCenter: blackBarCenter.toFixed(0) + 'px',
         horizontalOffset: (index * 100) + 'px',
         viewportDiagonal: viewportDiagonal.toFixed(0) + 'px'
       });
    } else {
      // Hide line if not active
      line.style.opacity = '0';
    }
  });
}

/* ==========================================================================
   Control Functions
   ========================================================================== */

export function pauseScrollingLines() {
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
    console.log('⏸️ [DEBUG] Scrolling lines paused');
  }
}

export function resumeScrollingLines() {
  if (scrollHandler) {
    window.addEventListener('scroll', scrollHandler, { passive: true });
    console.log('▶️ [DEBUG] Scrolling lines resumed');
  }
}

export function resetScrollingLines() {
  console.log('🔄 [DEBUG] Resetting scrolling lines...');
  
  // Remove scroll listener
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
  }
  
  // Remove existing lines
  const container = document.getElementById('css-lines-container');
  if (container) {
    container.remove();
  }
  
  // Reinitialize
  setupScrollingLines();
  console.log('🔄 [DEBUG] Scrolling lines reset complete');
} 